import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { transcriptSyncIdentity } from '@/lib/transcripts/persistence';
import { transcriptBubbleId } from '@/lib/transcriptBubbleId';

// LT-IDENTITY-DEDUP (catalog B1, 2026-09-10): the ASCII text normaliser and the
// word-set similarity that lived here are gone. They keyed six overlapping
// duplicate guards on TEXT, and the normaliser stripped every non-ASCII code
// point, so for zh/ja/ko/ru the guards were blind or wrong (two Mandarin
// sentences sharing one Latin token collapsed to that token and the second
// was dropped). Identity is the key now: the backend's segment id and
// sequence. Nothing else.

// The audio-window duplicate guard that stood here is gone too (review of
// catalog B1, 2026-09-10): Deepgram's word offsets restart at zero on every
// STT stream, and restarts are routine early in a call, so two genuine
// utterances minutes apart can share a window; without text the window was
// the only key and it dropped real speech. Its premise, two backend workers
// transcribing one track, is what the backend's restart sentinel prevents and
// rule 52 forbids — a routinely-active fallback is the defect, not a guard.

export type TranscriptionMessage = {
  /** Immutable persistence identity; never replace it during interim/final updates. */
  syncId?: string;
  name: string;
  message: string;
  timestamp: number;
  isSelf: boolean;
  isOfficer?: boolean; // Whether speaker is officer/staff (from transcriber is_officer flag)
  color: string;
  initial: string;
  isFinal?: boolean; // Track if this is a final or interim transcript
  participantIdentity?: string;
  segmentId?: string; // Unique ID to track segments across interim/final updates
  segmentSeq?: number; // Monotonically increasing sequence for ordering validation
  segmentKey?: string; // Stable key (segmentId or synthetic) used internally
  bubbleId?: string; // Groups consecutive segments from same speaker into UI bubbles
  audioStartTime?: number; // Deepgram seconds from audio stream start
  audioEndTime?: number; // Deepgram seconds from audio stream start
  /**
   * Rule 57: a rescue is flagged as such. 'flush' = the backend's silence
   * flush published the provider's interim as this final; 'tail' = a
   * stream-ending close carried the provider's last interim out with it.
   * Undefined for a provider final. Persisted with the row so the after-call
   * transcript can tell a rescue from a provider final.
   */
  rescue?: 'flush' | 'tail';
};

// Transcription status for accurate status indicator
export type TranscriptionStatusValue =
  | 'connecting'
  | 'waiting'
  | 'transcribing'
  | 'paused'
  | 'error'
  | 'unavailable';

export interface TranscriptionStatus {
  status: TranscriptionStatusValue;
  details?: {
    message?: string;
    lastTranscriptTime?: number;
    audioSource?: string;
    errorType?: string;
    participantIdentity?: string;
    trackSid?: string;
    firstAudioAtMs?: number;
    firstNonSilentAtMs?: number;
    lastFrameMaxAbs?: number;
    traceId?: string;
    /** The failure reason of the participant this status names (the backend's sttFailures carry it for everyone). */
    reason?: string;
  };
  /**
   * Every participant whose speech-to-text stream is failed right now (Q-433).
   * The backend sends it with every status; absent on older backends.
   * `sinceMs` is when that participant's current failure began.
   */
  sttFailures?: SttFailure[];
  /** Start of the current continuous STT-error episode (null when nobody is failed); the A65 stamp's key. */
  errorSinceMs?: number | null;
  /**
   * Error episodes that ENDED and that no successfully sent payload has carried yet (C-REC F4a): an episode
   * that started and ended between two sends still reaches the room. Re-sent until a send succeeds, so the
   * same episode can arrive more than once.
   */
  endedErrorEpisodes?: EndedSttErrorEpisode[];
  /** The sending agent's instance, and its monotonic payload number: a late arrival is dropped (Q-433). */
  epoch?: number;
  seq?: number;
  timestamp: number;
}

export interface SttFailure {
  participantIdentity: string;
  reason?: string;
  sinceMs?: number;
}

export interface EndedSttErrorEpisode {
  sinceMs: number;
  endedMs?: number | null;
  reasons?: string[];
  /**
   * How it ended (C-REC STT-R1): 'recovered' only when every participant failed in it recovered;
   * 'participant_left' (or another cause, or none) otherwise. Only 'recovered' lets the officer be told so.
   */
  endCause?: string | null;
}

/**
 * One speech-to-text error episode this page has heard of (C-REC F4a), keyed by its start (errorSinceMs, the
 * A65 stamp key `stt:error:<sinceMs>`). Fed by every live errorSinceMs and every endedErrorEpisodes entry; a
 * later status never removes or overwrites one, so an episode that came and went before the pill rendered it
 * is still here for the pill to tell the officer about.
 */
export interface SttErrorEpisode {
  sinceMs: number;
  /** When it ended, once a payload said so; null while only seen live. */
  endedMs: number | null;
  /** Every reason reported in it, first-seen order, whole (never truncated). No participant identity. */
  reasons: string[];
  /** How it ended, once a payload said so (STT-R1); null while unknown. Only 'recovered' is told as recovered. */
  endCause: string | null;
  /** The officer was shown it: the pill painted it live, or painted its "was interrupted" notice. */
  shown: boolean;
}

// Active bubble tracking per speaker for bubble consolidation
export interface ActiveBubble {
  bubbleId: string;
  startTime: number;
}

export interface TranscriptionState {
  transcripts: TranscriptionMessage[];
  // Track the highest segment_seq seen per segmentKey to detect out-of-order messages
  lastSeqBySegment: Record<string, number>;
  // Ring buffer of recent hashes to drop exact replays
  /** Dead since catalog B1 (2026-09-10): the interim hash ring is gone; the field stays until the persisted state shape is versioned (TL-15). */
  recentHashes: string[];
  // Current transcription status for status indicator
  transcriptionStatus: TranscriptionStatus;
  // Track active bubble per speaker for UI grouping (60s timeout or speaker switch)
  activeBubbles: Record<string, ActiveBubble>;
  // CRITICAL: Track finalized segment IDs - never show interims for these segments
  finalizedSegmentIds: Record<string, number>; // segmentId -> timestamp when finalized
  /**
   * Interims whose SPEAKER LEFT THE ROOM while they were still open, keyed by
   * {@link interimEndKey}. Nothing more can arrive for them, so they no longer
   * make their bubble "still receiving" — their words stay on screen and the
   * row settles like any other. Without this the caller's last caption, cut
   * off by a hang-up before its final arrived, stayed interim forever and the
   * caption lifecycle (which never retires an interim row) kept it on the
   * video for the rest of the call (Andrew, staging 2026-09-23: "the very last
   * closed captioned container gets stuck open and never goes away").
   * A late final for the segment still settles it through the normal path.
   * Optional because synced/hydrated state predating it carries none.
   */
  endedInterimKeys?: Record<string, true>;
  /** Per-call STT error episode log, keyed by String(sinceMs); see {@link SttErrorEpisode}. Optional: older state has none. */
  sttErrorEpisodes?: Record<string, SttErrorEpisode>;
}

/**
 * The key an open interim is ended under when its speaker leaves. The segment
 * id when the wire carried one (every ingest row does); otherwise the speaker
 * and the row's own stamp, which is all such a row has to be told apart by.
 */
export function interimEndKey(message: TranscriptionMessage): string {
  return (
    message.segmentId ||
    message.segmentKey ||
    `${message.participantIdentity || message.name}@${message.timestamp}`
  );
}

// Bubble timeout: start new bubble after 60 seconds of continuous speech
const BUBBLE_TIMEOUT_MS = 60000;

const initialState: TranscriptionState = {
  transcripts: [],
  lastSeqBySegment: {},
  recentHashes: [],
  transcriptionStatus: {
    status: 'connecting',
    details: { message: 'Initializing...' },
    timestamp: Date.now(),
  },
  activeBubbles: {},
  finalizedSegmentIds: {},
};

function addReason(reasons: string[], reason: unknown): void {
  if (typeof reason === 'string' && reason !== '' && !reasons.includes(reason)) reasons.push(reason);
}

function upsertSttErrorEpisode(state: TranscriptionState, sinceMs: number): SttErrorEpisode {
  const log = (state.sttErrorEpisodes ??= {});
  const key = String(sinceMs);
  return (log[key] ??= { sinceMs, endedMs: null, reasons: [], endCause: null, shown: false });
}

/** Record the episode a status is live in, and every ended episode it carries (C-REC F4a). */
function logSttErrorEpisodes(state: TranscriptionState, status: TranscriptionStatus): void {
  if (typeof status.errorSinceMs === 'number' && Number.isFinite(status.errorSinceMs)) {
    const episode = upsertSttErrorEpisode(state, status.errorSinceMs);
    for (const failure of status.sttFailures ?? []) addReason(episode.reasons, failure?.reason);
    if (!status.sttFailures?.length) addReason(episode.reasons, status.details?.reason);
  }
  if (!Array.isArray(status.endedErrorEpisodes)) return;
  for (const ended of status.endedErrorEpisodes) {
    if (!ended || typeof ended.sinceMs !== 'number' || !Number.isFinite(ended.sinceMs)) continue;
    const episode = upsertSttErrorEpisode(state, ended.sinceMs);
    if (typeof ended.endedMs === 'number') episode.endedMs = ended.endedMs;
    if (typeof ended.endCause === 'string' && ended.endCause !== '') episode.endCause = ended.endCause;
    for (const reason of ended.reasons ?? []) addReason(episode.reasons, reason);
  }
}

const NO_STT_ERROR_EPISODES: Record<string, SttErrorEpisode> = {};

/** The call's STT error episode log (stable empty object when there is none). */
export const selectSttErrorEpisodes = (state: { transcription?: Partial<TranscriptionState> }): Record<string, SttErrorEpisode> =>
  state.transcription?.sttErrorEpisodes ?? NO_STT_ERROR_EPISODES;

const transcriptionSlice = createSlice({
  name: 'transcription',
  initialState,
  reducers: {
    setTranscripts: (state, action: PayloadAction<TranscriptionMessage[]>) => {
      state.transcripts = action.payload.map((transcript, index) => ({
        ...transcript,
        syncId: transcriptSyncIdentity(transcript, index),
      }));
    },

    /**
     * Merge incoming transcripts by segmentKey (or timestamp:message fallback).
     * Only appends items that don't already exist in state. Sorts by timestamp after merge.
     * Used by Realtime handler to avoid wholesale replacement of live transcript state.
     */
    mergeTranscripts: (state, action: PayloadAction<TranscriptionMessage[]>) => {
      // Index existing transcripts by key -> array index (for in-place replacement).
      const indexByKey = new Map<string, number>();
      state.transcripts.forEach((t, i) => {
        if (t.syncId) indexByKey.set(t.syncId, i);
        if (t.segmentKey) indexByKey.set(t.segmentKey, i);
        indexByKey.set(`${t.timestamp}:${t.message}`, i);
      });

      for (const [incomingIndex, rawIncoming] of action.payload.entries()) {
        const incoming: TranscriptionMessage = {
          ...rawIncoming,
          syncId: transcriptSyncIdentity(rawIncoming, incomingIndex),
        };
        // Mirror updateOrAddTranscript's gate (see finalizedSegmentIds check):
        // never resurrect an interim for a segment that has already been
        // finalized (live, or via an earlier item in this merge).
        if (
          !incoming.isFinal &&
          incoming.segmentId &&
          state.finalizedSegmentIds[incoming.segmentId]
        ) {
          continue;
        }

        const fallbackKey = `${incoming.timestamp}:${incoming.message}`;
        const existingIdx =
          (incoming.syncId ? indexByKey.get(incoming.syncId) : undefined) ??
          (incoming.segmentKey ? indexByKey.get(incoming.segmentKey) : undefined) ??
          indexByKey.get(fallbackKey);
        if (existingIdx === undefined) {
          indexByKey.set(incoming.syncId!, state.transcripts.length);
          if (incoming.segmentKey) {
            indexByKey.set(incoming.segmentKey, state.transcripts.length);
          }
          indexByKey.set(fallbackKey, state.transcripts.length);
          state.transcripts.push(incoming);
        } else {
          const existing = state.transcripts[existingIdx];
          const incomingSeq = incoming.segmentSeq;
          const existingSeq = existing.segmentSeq;
          const hasNewerSourceSequence =
            typeof incomingSeq === 'number' &&
            Number.isFinite(incomingSeq) &&
            typeof existingSeq === 'number' &&
            Number.isFinite(existingSeq) &&
            incomingSeq > existingSeq;
          const shouldReplace =
            (incoming.isFinal && !existing.isFinal) ||
            (incoming.isFinal === existing.isFinal && hasNewerSourceSequence);
          if (!shouldReplace) continue;

          // A DB FINAL replaces an interim, and a higher source sequence
          // replaces the same-finality version. Never downgrade final→interim.
          state.transcripts[existingIdx] = {
            ...incoming,
            syncId: existing.syncId ?? incoming.syncId,
          };
        }
      }

      // Record merged finals so a late post-reconnect LiveKit interim for the
      // same segment is rejected by updateOrAddTranscript's finalizedSegmentIds
      // gate. Normalize to ms (mirrors the timestamp normalization elsewhere in
      // this slice) so the 5-minute retention GC doesn't prune the entry
      // immediately when a seconds-valued timestamp slips through.
      for (const incoming of action.payload) {
        if (
          incoming.isFinal &&
          incoming.segmentId &&
          !state.finalizedSegmentIds[incoming.segmentId]
        ) {
          const tsMs = incoming.timestamp
            ? incoming.timestamp < 1e12
              ? incoming.timestamp * 1000
              : incoming.timestamp
            : Date.now();
          state.finalizedSegmentIds[incoming.segmentId] = tsMs;
        }
      }

      // Sort by timestamp after merge
      state.transcripts.sort((a, b) => a.timestamp - b.timestamp);
    },

    /**
     * Project authoritative same-key conflict winners returned by transcript
     * persistence into Redux. Unlike reconnect merge, this replaces final with
     * final so metadata/text corrections cannot be revived by the next sync.
     */
    reconcilePersistedTranscripts: (state, action: PayloadAction<TranscriptionMessage[]>) => {
      const indexByKey = new Map<string, number>();
      state.transcripts.forEach((transcript, index) => {
        if (transcript.syncId) indexByKey.set(transcript.syncId, index);
        if (transcript.segmentId) indexByKey.set(transcript.segmentId, index);
        if (transcript.segmentKey) indexByKey.set(transcript.segmentKey, index);
      });

      action.payload.forEach((rawIncoming, incomingIndex) => {
        const incoming: TranscriptionMessage = {
          ...rawIncoming,
          syncId: transcriptSyncIdentity(rawIncoming, incomingIndex),
        };
        const existingIndex =
          (incoming.syncId ? indexByKey.get(incoming.syncId) : undefined) ??
          (incoming.segmentId ? indexByKey.get(incoming.segmentId) : undefined) ??
          (incoming.segmentKey ? indexByKey.get(incoming.segmentKey) : undefined);

        if (existingIndex === undefined) {
          const nextIndex = state.transcripts.length;
          state.transcripts.push(incoming);
          indexByKey.set(incoming.syncId!, nextIndex);
          if (incoming.segmentId) indexByKey.set(incoming.segmentId, nextIndex);
          if (incoming.segmentKey) indexByKey.set(incoming.segmentKey, nextIndex);
          if (incoming.isFinal && incoming.segmentId) {
            state.finalizedSegmentIds[incoming.segmentId] =
              incoming.timestamp < 1e12 ? incoming.timestamp * 1000 : incoming.timestamp;
          }
          return;
        }

        const existing = state.transcripts[existingIndex];
        if (existing.isFinal && !incoming.isFinal) return;
        state.transcripts[existingIndex] = incoming;
        if (incoming.isFinal && incoming.segmentId) {
          state.finalizedSegmentIds[incoming.segmentId] =
            incoming.timestamp < 1e12 ? incoming.timestamp * 1000 : incoming.timestamp;
        }
      });

      state.transcripts.sort((a, b) => a.timestamp - b.timestamp);
    },

    clearTranscripts: (state) => {
      state.transcripts = [];
      state.lastSeqBySegment = {};
      state.recentHashes = [];
      state.activeBubbles = {};
      state.finalizedSegmentIds = {};
      state.endedInterimKeys = {};
      state.sttErrorEpisodes = {};
      // Reset status to connecting when clearing
      state.transcriptionStatus = {
        status: 'connecting',
        details: { message: 'Initializing...' },
        timestamp: Date.now(),
      };
    },

    setTranscriptionStatus: (state, action: PayloadAction<TranscriptionStatus>) => {
      // Status publishes can arrive out of order (Q-433 reviews): only a newer payload replaces the one shown —
      // a higher seq from the same agent instance, or any seq from a NEWER instance (epoch). Payloads without
      // epoch/seq (an older backend) are shown in arrival order, as before.
      const shown = state.transcriptionStatus;
      const next = action.payload;
      // Logged BEFORE the ordering check: an episode is a fact whichever payload carries it, and a late or
      // superseded payload may be the only one that does (C-REC F4a).
      logSttErrorEpisodes(state, next);
      if (next.epoch !== undefined && shown.epoch !== undefined && next.epoch < shown.epoch) {
        return; // a late packet from an agent instance that has been replaced
      }
      if (next.epoch !== undefined && next.epoch === shown.epoch
          && next.seq !== undefined && shown.seq !== undefined && next.seq <= shown.seq) {
        return;
      }
      state.transcriptionStatus = next;
    },

    /** The officer was shown this STT error episode (live, or as its notice): it gets no further notice. */
    markSttErrorEpisodeShown: (state, action: PayloadAction<number>) => {
      const episode = state.sttErrorEpisodes?.[String(action.payload)];
      if (episode) episode.shown = true;
    },

    /**
     * A participant left the room: every interim of theirs still open is
     * over. Records their keys (see `endedInterimKeys`); the text is kept.
     * A rejoin speaks under new segment ids, so it is live again untouched.
     */
    speakerDeparted: (state, action: PayloadAction<string>) => {
      const identity = action.payload;
      if (!identity) return;
      for (const message of state.transcripts) {
        if (message.isFinal || message.participantIdentity !== identity) continue;
        (state.endedInterimKeys ??= {})[interimEndKey(message)] = true;
      }
    },

    hydrateTranscriptionState: (_state, action: PayloadAction<TranscriptionState>) => {
      return {
        ...action.payload,
        transcripts: action.payload.transcripts.map((transcript, index) => ({
          ...transcript,
          syncId: transcriptSyncIdentity(transcript, index),
        })),
      };
    },

    // Simple addTranscript for loading from localStorage (backwards compat)
    // Assigns bubbleId if missing, grouping consecutive same-speaker messages
    addTranscript: (state, action: PayloadAction<TranscriptionMessage>) => {
      const incoming = action.payload;
      incoming.syncId = transcriptSyncIdentity(incoming, state.transcripts.length);
      // Dedupe by checking if an identical transcript already exists
      const exists = state.transcripts.some(
        (t) =>
          t.message === incoming.message &&
          t.name === incoming.name &&
          t.timestamp === incoming.timestamp,
      );
      if (!exists) {
        // Assign bubbleId if missing (e.g., older localStorage data)
        if (!incoming.bubbleId) {
          const speakerId = incoming.participantIdentity || incoming.name || 'unknown';
          const speakerBubble = state.activeBubbles[speakerId];

          // Check if another speaker has spoken since our last message
          const lastMsgFromUs = [...state.transcripts]
            .reverse()
            .find((t) => (t.participantIdentity || t.name) === speakerId);
          const otherSpeakerSpokeSince = state.transcripts.some((t) => {
            const tSpeaker = t.participantIdentity || t.name;
            return tSpeaker !== speakerId && lastMsgFromUs && t.timestamp > lastMsgFromUs.timestamp;
          });

          const normalizedTimestamp =
            incoming.timestamp < 1e12 ? incoming.timestamp * 1000 : incoming.timestamp;

          const needsNewBubble =
            !speakerBubble ||
            normalizedTimestamp - speakerBubble.startTime > BUBBLE_TIMEOUT_MS ||
            otherSpeakerSpokeSince;

          if (needsNewBubble) {
            const bubbleId = transcriptBubbleId(speakerId, normalizedTimestamp);
            state.activeBubbles[speakerId] = { bubbleId, startTime: normalizedTimestamp };
            incoming.bubbleId = bubbleId;
          } else {
            incoming.bubbleId = speakerBubble.bubbleId;
          }
        }

        state.transcripts.push(incoming);
        // Keep sorted by timestamp
        state.transcripts.sort((a, b) => a.timestamp - b.timestamp);
      }
    },

    updateOrAddTranscript: (
      state,
      action: PayloadAction<{
        transcript: TranscriptionMessage;
        isFinal: boolean;
        participantIdentity: string;
        segmentId?: string;
        segmentSeq?: number;
        rawTimestamp?: number;
      }>,
    ) => {
      const {
        transcript: incoming,
        isFinal,
        participantIdentity,
        segmentId,
        segmentSeq,
        rawTimestamp,
      } = action.payload;

      // Normalize timestamp to ms
      const normalizedTimestamp = (() => {
        if (!incoming.timestamp) return Date.now();
        // If timestamp looks like seconds, convert to ms
        return incoming.timestamp < 1e12 ? incoming.timestamp * 1000 : incoming.timestamp;
      })();

      const speakerId = participantIdentity || incoming.participantIdentity || 'unknown';

      // CRITICAL FIX: Reject ANY interim for a finalized segment ID
      // This is the FIRST check - before all other dedup logic
      // Ground truth rule: "Never show an interim for a segment that already has a final"
      if (!isFinal && segmentId && state.finalizedSegmentIds[segmentId]) {
        return; // DROP - segment already finalized
      }

      // NOTE: The old "recentFinalWindowMs" check that dropped ALL interims from a speaker
      // within 1.5s of any final was REMOVED. It silently dropped new-segment interims in
      // continuous speech (Deepgram starts segment B immediately after finalizing segment A).
      // The finalizedSegmentIds check above correctly handles per-segment protection.

      // Compute segmentKey: prefer backend segmentId; otherwise synthesize per speaker/time bucket
      const syntheticBucket = Math.floor(normalizedTimestamp / 500); // 500ms buckets
      const segmentKey = segmentId || `${speakerId}-${syntheticBucket}`;

      // Is this an update to an existing line? Identity only: the segment key.
      const existingIdx = state.transcripts.findIndex((t) => t.segmentKey === segmentKey);

      const isSegmentUpdate = existingIdx >= 0;

      // === BUBBLE ASSIGNMENT ===
      // Determine if we need a new bubble or continue existing one
      let assignedBubbleId: string;
      const speakerBubble = state.activeBubbles[speakerId];

      // Check if another speaker has spoken since our last message (speaker switch)
      const lastMsgFromUs = [...state.transcripts]
        .reverse()
        .find((t) => (t.participantIdentity || t.name) === speakerId);
      const otherSpeakerSpokeSince = state.transcripts.some((t) => {
        const tSpeaker = t.participantIdentity || t.name;
        return tSpeaker !== speakerId && lastMsgFromUs && t.timestamp > lastMsgFromUs.timestamp;
      });

      // Need new bubble if:
      // 1. No active bubble for this speaker, OR
      // 2. Timeout exceeded (60 seconds since bubble started), OR
      // 3. Another speaker spoke since our last message
      const needsNewBubble =
        !speakerBubble ||
        normalizedTimestamp - speakerBubble.startTime > BUBBLE_TIMEOUT_MS ||
        otherSpeakerSpokeSince;

      if (needsNewBubble) {
        assignedBubbleId = transcriptBubbleId(speakerId, normalizedTimestamp);
        state.activeBubbles[speakerId] = {
          bubbleId: assignedBubbleId,
          startTime: normalizedTimestamp,
        };
      } else {
        assignedBubbleId = speakerBubble.bubbleId;
      }

      const transcriptWithData: TranscriptionMessage = {
        ...incoming,
        syncId:
          (existingIdx >= 0 ? state.transcripts[existingIdx].syncId : undefined) ??
          incoming.syncId ??
          transcriptSyncIdentity(
            {
              ...incoming,
              participantIdentity: speakerId,
              segmentId,
              segmentKey,
              timestamp: normalizedTimestamp,
            },
            state.transcripts.length,
          ),
        isFinal,
        participantIdentity: speakerId,
        segmentId,
        segmentSeq,
        segmentKey,
        timestamp: normalizedTimestamp,
        bubbleId: assignedBubbleId,
      };

      // Handle existing segment update or new segment
      if (isSegmentUpdate) {
        const existing = state.transcripts[existingIdx];

        // If we already finalized this segment, drop any interim
        if (existing.isFinal && !isFinal) {
          return;
        }

        // Sequence enforcement per segmentKey
        const lastSeq = state.lastSeqBySegment[segmentKey] ?? -Infinity;
        if (segmentSeq !== undefined) {
          if (segmentSeq < lastSeq) {
            return;
          }
          state.lastSeqBySegment[segmentKey] = segmentSeq;
        }

        // If incoming is final, lock the segment; otherwise update interim
        // IMPORTANT: Preserve the original bubbleId - segment already belongs to a bubble
        state.transcripts[existingIdx] = {
          ...existing,
          ...transcriptWithData,
          isFinal: existing.isFinal || isFinal,
          bubbleId: existing.bubbleId || transcriptWithData.bubbleId, // Keep original bubble assignment
        };

        // CRITICAL: Track finalized segment ID to reject future interims
        if (isFinal && segmentId) {
          state.finalizedSegmentIds[segmentId] = normalizedTimestamp;
        }
      } else {
        // New segment
        if (segmentSeq !== undefined) {
          state.lastSeqBySegment[segmentKey] = segmentSeq;
        }
        state.transcripts.push(transcriptWithData);

        // CRITICAL: Track finalized segment ID for new finals
        if (isFinal && segmentId) {
          state.finalizedSegmentIds[segmentId] = normalizedTimestamp;
        }
      }

      // Memory cleanup: prune old finalized segment IDs (keep for 5 minutes max)
      const FINALIZED_RETENTION_MS = 5 * 60 * 1000;
      const now = Date.now();
      for (const key of Object.keys(state.finalizedSegmentIds)) {
        if (now - state.finalizedSegmentIds[key] > FINALIZED_RETENTION_MS) {
          delete state.finalizedSegmentIds[key];
        }
      }

      // Sorting is redundant here as selectTranscriptionBubbles handles it,
      // and checking/sorting purely by timestamp here is expensive on every update.
      // We rely on the selector to output bubbles in correct order.
    },
  },
});

export const {
  addTranscript,
  setTranscripts,
  mergeTranscripts,
  reconcilePersistedTranscripts,
  clearTranscripts,
  setTranscriptionStatus,
  markSttErrorEpisodeShown,
  speakerDeparted,
  hydrateTranscriptionState,
  updateOrAddTranscript,
} = transcriptionSlice.actions;

export default transcriptionSlice.reducer;

// === BUBBLE SELECTOR ===
// Groups transcripts by bubbleId for consolidated UI rendering

export interface TranscriptionBubble {
  bubbleId: string;
  speaker: string; // participantIdentity or name
  speakerName: string; // Display name
  startTime: number;
  endTime: number;
  messages: TranscriptionMessage[];
  combinedText: string; // All final texts joined, or latest interim if no finals
  settledText: string; // Finalized text only; never becomes interim again
  interimText: string; // Current unfinalized tail only
  hasInterim: boolean; // true if any message is still interim (still receiving)
  isSelf: boolean; // Is this the local user?
  color: string; // Speaker color
  initial: string; // Speaker initial
}

// Selector to get raw transcripts (for memoization input)
const selectTranscripts = (state: { transcription: TranscriptionState }) =>
  state.transcription.transcripts;

const NO_ENDED_INTERIMS: Readonly<Record<string, true>> = Object.freeze({});
const selectEndedInterimKeys = (state: { transcription: TranscriptionState }) =>
  state.transcription.endedInterimKeys ?? NO_ENDED_INTERIMS;

/**
 * Memoized selector that groups transcripts into bubbles.
 * Only recomputes when the transcripts array changes.
 */
export const selectTranscriptionBubbles = createSelector(
  [selectTranscripts, selectEndedInterimKeys],
  (
    transcripts: TranscriptionMessage[],
    endedInterimKeys: Readonly<Record<string, true>>,
  ): TranscriptionBubble[] => {
    // Pre-compute bubble IDs for transcripts that lack them (e.g., loaded from localStorage
    // via addTranscript which doesn't run bubble assignment). Groups consecutive same-speaker
    // messages into a shared bubble, matching the live session's grouping behavior.
    const hasMissingBubbleIds = transcripts.some((t) => !t.bubbleId);
    let resolvedTranscripts = transcripts;

    if (hasMissingBubbleIds) {
      const sorted = [...transcripts].sort((a, b) => a.timestamp - b.timestamp);
      let currentBubbleId = '';
      let currentSpeaker = '';
      let bubbleStartTime = 0;

      resolvedTranscripts = sorted.map((msg) => {
        if (msg.bubbleId) {
          // Already has bubble ID — track speaker for continuity
          currentSpeaker = msg.participantIdentity || msg.name || 'Unknown';
          currentBubbleId = msg.bubbleId;
          bubbleStartTime = msg.timestamp;
          return msg;
        }

        const speaker = msg.participantIdentity || msg.name || 'Unknown';
        // New bubble if: speaker changed or 60s timeout exceeded
        if (speaker !== currentSpeaker || msg.timestamp - bubbleStartTime > BUBBLE_TIMEOUT_MS) {
          currentBubbleId = `computed-${speaker}-${msg.timestamp}`;
          currentSpeaker = speaker;
          bubbleStartTime = msg.timestamp;
        }
        return { ...msg, bubbleId: currentBubbleId };
      });
    }

    const bubbleMap = new Map<string, TranscriptionBubble>();

    for (const msg of resolvedTranscripts) {
      const bubbleId = msg.bubbleId!;
      const speaker = msg.participantIdentity || msg.name || 'Unknown';

      if (!bubbleMap.has(bubbleId)) {
        bubbleMap.set(bubbleId, {
          bubbleId,
          speaker,
          speakerName: msg.name,
          startTime: msg.timestamp,
          endTime: msg.timestamp,
          messages: [],
          combinedText: '',
          settledText: '',
          interimText: '',
          hasInterim: false,
          isSelf: msg.isSelf,
          color: msg.color,
          initial: msg.initial,
        });
      }

      const bubble = bubbleMap.get(bubbleId)!;
      bubble.messages.push(msg);
      bubble.endTime = Math.max(bubble.endTime, msg.timestamp);
      bubble.hasInterim = bubble.hasInterim || !msg.isFinal;
    }

    // Build combined text for each bubble
    // CRITICAL: Never show empty text - always preserve something visible
    // CRITICAL FIX: Never show interims for segments that have finals
    for (const bubble of bubbleMap.values()) {
      // Sort messages by timestamp (chronological order)
      const sorted = [...bubble.messages].sort((a, b) => a.timestamp - b.timestamp);

      // STEP 1: Build set of finalized segment IDs within this bubble
      // Ground truth rule: "Never show an interim for a segment that already has a final"
      const finalizedSegmentIds = new Set<string>();
      for (const msg of sorted) {
        if (msg.isFinal) {
          if (msg.segmentId) finalizedSegmentIds.add(msg.segmentId);
          if (msg.segmentKey) finalizedSegmentIds.add(msg.segmentKey);
        }
      }

      // STEP 2: Filter messages - drop interims for finalized segments
      const validMessages = sorted.filter((m) => {
        if (m.isFinal) return true; // Finals are always valid
        // Interim: check if its segment has been finalized
        const interimSegmentId = m.segmentId || m.segmentKey;
        if (interimSegmentId && finalizedSegmentIds.has(interimSegmentId)) {
          // This interim belongs to a finalized segment - skip it
          return false;
        }
        return true; // Non-finalized interim is valid
      });

      const finals = validMessages.filter((m) => m.isFinal && m.message?.trim());
      const interims = validMessages.filter((m) => !m.isFinal && m.message?.trim());

      // Helper: get latest interim per segment (prevents losing text from earlier segments)
      const getLatestPerSegment = (msgs: TranscriptionMessage[]): TranscriptionMessage[] => {
        const bySegment = new Map<string, TranscriptionMessage>();
        for (const m of msgs) {
          const key = m.segmentId || m.segmentKey || `ts-${m.timestamp}`;
          const existing = bySegment.get(key);
          if (!existing || m.timestamp >= existing.timestamp) {
            bySegment.set(key, m);
          }
        }
        return Array.from(bySegment.values()).sort((a, b) => a.timestamp - b.timestamp);
      };

      // Build combined text, ensuring we NEVER show empty
      let combinedText = '';
      const settledText = finals
        .map((m) => m.message)
        .join(' ')
        .trim();
      let interimText = '';
      // The interims that form the bubble's in-flight TAIL — the only ones that
      // mean it is still receiving (see `hasInterim` below).
      let tailInterims: TranscriptionMessage[] = [];

      if (finals.length > 0) {
        // Show all finalized text
        combinedText = settledText;

        // If there are newer interims AFTER the last final, append them
        // This handles the case where user is still speaking after some finals arrived
        // Note: These interims are already filtered - they don't belong to finalized segments
        // FIX: Show latest per segment (not just the last overall) to avoid losing text
        // from multiple concurrent segments
        const lastFinalTimestamp = finals[finals.length - 1]?.timestamp || 0;
        const trailingInterims = interims.filter((m) => m.timestamp > lastFinalTimestamp);
        tailInterims = trailingInterims;
        if (trailingInterims.length > 0) {
          const latestPerSegment = getLatestPerSegment(trailingInterims);
          interimText = latestPerSegment
            .map((m) => m.message)
            .join(' ')
            .trim();
          if (interimText) combinedText += ' ' + interimText;
        }
      } else if (interims.length > 0) {
        // No finals yet - show latest interim per segment, joined chronologically.
        // FIX: Previously only showed the LAST interim overall, which lost text from
        // earlier segments when a new segment started before the old one was finalized.
        // This caused the "huge chunk replacement" bug on officer transcripts.
        tailInterims = interims;
        const latestPerSegment = getLatestPerSegment(interims);
        interimText = latestPerSegment
          .map((m) => m.message)
          .join(' ')
          .trim();
        combinedText = interimText;
      }

      // SAFETY: Never clear visible text - if combinedText is empty but we have messages,
      // fall back to ANY non-empty message (prevents flickering during transitions)
      if (!combinedText.trim() && bubble.messages.length > 0) {
        const anyNonEmpty = bubble.messages.find((m) => m.message?.trim());
        combinedText = anyNonEmpty?.message || '';
      }

      // hasInterim = "still receiving": true only for the TAIL interims built into
      // interimText above, and only those still OPEN.
      //  - An unfinalized interim OLDER than the bubble's last final is not the tail:
      //    the speaker has moved on past it, nothing renders it, and a newer word for
      //    its segment would arrive later and become the tail. Counting it pinned the
      //    row as interim forever — the caption lifecycle never retires an interim
      //    row, so the call's last caption stayed on the video (Andrew, staging
      //    2026-09-23: "the very last closed captioned container gets stuck open").
      //  - An interim whose speaker left the room keeps its words above but can
      //    receive nothing more (see `endedInterimKeys`).
      bubble.hasInterim = tailInterims.some((m) => !endedInterimKeys[interimEndKey(m)]);

      bubble.combinedText = combinedText;
      bubble.settledText = settledText;
      bubble.interimText = interimText;
    }

    // Return sorted by start time
    return Array.from(bubbleMap.values()).sort((a, b) => a.startTime - b.startTime);
  },
);
